import React from 'react';
import { Stack, Typography, Card, CardContent, Box, Chip, Collapse } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { ORG_EXPECTED_FILES_WITH_HEADERS } from './ExpectedFilesList';
import { getHeaders } from './Controllers/fileUtils';
import FileItem from './FileItem';
import { FileHeaderLoader, FileMetadataLoader } from './hooks/useFileMetadata';

function FileGroupingCard({ 
    month,
    files,
    isCollapsed,
    onToggleMonth,
    cardTitle,
    pendingReplacement = null,
    organizationId = null,
    fileType,
    onFilesUpdate,
    onHeaderLoadingChange,
    historyDate,
}) {
    const theme = useTheme();
    const [fileHeaders, setFileHeaders] = React.useState({});
    const [fileMetadata, setFileMetadata] = React.useState({});
    const [currentLoadingFile, setCurrentLoadingFile] = React.useState(null);
    const [currentLoadingMetadataFile, setCurrentLoadingMetadataFile] = React.useState(null);
    
    // Get files that need headers loaded (uploaded files without headers)
    const filesNeedingHeaders = React.useMemo(() => {
        return files.filter(file => 
            file.file_name && 
            organizationId && 
            !file.isPending && 
            !fileHeaders[file.file_name]
        );
    }, [files, organizationId, fileHeaders]);
    
    // Get files that need metadata loaded (uploaded files without metadata)
    const filesNeedingMetadata = React.useMemo(() => {
        return files.filter(file => 
            file.file_name && 
            organizationId && 
            !file.isPending && 
            !fileMetadata[file.file_name]
        );
    }, [files, organizationId, fileMetadata]);
    
    // Memoize the first file that needs headers to avoid unnecessary re-renders
    const nextFileToLoad = React.useMemo(() => {
        return filesNeedingHeaders.length > 0 ? filesNeedingHeaders[0] : null;
    }, [filesNeedingHeaders]);

    const isHeaderLoading = React.useMemo(() => {
        return Boolean(currentLoadingFile) || filesNeedingHeaders.length > 0;
    }, [currentLoadingFile, filesNeedingHeaders.length]);
    
    // Memoize the first file that needs metadata
    const nextMetadataFileToLoad = React.useMemo(() => {
        return filesNeedingMetadata.length > 0 ? filesNeedingMetadata[0] : null;
    }, [filesNeedingMetadata]);
    
    // Set the next file to load headers for
    React.useEffect(() => {
        if (!currentLoadingFile && nextFileToLoad) {
            setCurrentLoadingFile(nextFileToLoad);
        }
    }, [currentLoadingFile, nextFileToLoad]);
    
    // Set the next file to load metadata for
    React.useEffect(() => {
        if (!currentLoadingMetadataFile && nextMetadataFileToLoad) {
            setCurrentLoadingMetadataFile(nextMetadataFileToLoad);
        }
    }, [currentLoadingMetadataFile, nextMetadataFileToLoad]);

    React.useEffect(() => {
        if (onHeaderLoadingChange) {
            onHeaderLoadingChange(isHeaderLoading);
        }
    }, [isHeaderLoading, onHeaderLoadingChange]);

    React.useEffect(() => {
        return () => {
            if (onHeaderLoadingChange) {
                onHeaderLoadingChange(false);
            }
        };
    }, [onHeaderLoadingChange]);
    
    // Callback to handle when headers are loaded for a file
    const handleHeadersLoaded = React.useCallback((fileKey, headers) => {
        setFileHeaders(prev => {
            const updatedHeaders = {
                ...prev,
                [fileKey]: headers
            };
            const enhancedFiles = files.map(file => {
                if (file.file_name === fileKey && headers.headers) {
                    return {
                        ...file,
                        header: headers.headers.join(',')
                    };
                }
                return file;
            });
            onFilesUpdate(enhancedFiles);
            
            return updatedHeaders;
        });
        // Clear current loading file to trigger loading the next one
        setCurrentLoadingFile(null);
    }, [files, onFilesUpdate]);
    
    // Callback to handle when metadata is loaded for a file
    const handleMetadataLoaded = React.useCallback((fileKey, metadata) => {
        setFileMetadata(prev => {
            const updatedMetadata = {
                ...prev,
                [fileKey]: metadata
            };
            
            const enhancedFiles = files.map(file => {
                if (file.file_name === fileKey) {
                    return {
                        ...file,
                        ...metadata
                    };
                }
                return file;
            });
            onFilesUpdate(enhancedFiles);
            
            return updatedMetadata;
        });
        // Clear current loading file to trigger loading the next one
        setCurrentLoadingMetadataFile(null);
    }, [files, onFilesUpdate]);
    
    // Function to check if an expected file type is satisfied in a given month
    const isExpectedFileSatisfied = React.useCallback((expectedHeaders, monthFiles) => {
        return monthFiles.some(file => {
            // For pending files, use the existing header field
            if (file.isPending && file.header) {
                const fileHeaders = getHeaders(file.header);
                return fileHeaders.length === expectedHeaders.length &&
                       fileHeaders.every(header => expectedHeaders.includes(header));
            }
            
            // For uploaded files, use async loaded headers
            const loadedHeaders = fileHeaders[file.file_name];
            if (loadedHeaders && loadedHeaders.headers && Array.isArray(loadedHeaders.headers)) {
                return loadedHeaders.headers.length === expectedHeaders.length &&
                       loadedHeaders.headers.every(header => expectedHeaders.includes(header));
            }
            
            // If no headers available yet, can't determine satisfaction
            return false;
        });
    }, [fileHeaders]); // Add fileHeaders as dependency

    // Get expected files for the organization
    const expectedFiles = organizationId ? ORG_EXPECTED_FILES_WITH_HEADERS[organizationId] || {} : {};
    const hasPendingFile = files.some(file => file.isPending);
    const uploadedFilesCount = files.filter(file => !file.isPending).length;

    return (
        <Card 
            variant="outlined"
            sx={{
                border: hasPendingFile ? `2px solid ${theme.palette.border.green}` : pendingReplacement ? `2px solid ${theme.palette.border.yellow}` : undefined,
            }}
        >
            <CardContent>
                <Stack spacing={2}>
                    <Box 
                        display="flex" 
                        alignItems="center" 
                        gap={1} 
                        sx={{ cursor: 'pointer' }}
                        onClick={() => onToggleMonth(month)}
                    >
                        <Typography variant='heavy' sx={{ fontSize: '16px' }}>
                            {cardTitle}
                        </Typography>
                        <Chip 
                            label={hasPendingFile 
                                ? `${uploadedFilesCount} file${uploadedFilesCount !== 1 ? 's' : ''} + 1 to upload`
                                : `${files.length} file${files.length > 1 ? 's' : ''}`
                            } 
                            size="small" 
                            color={hasPendingFile ? 'success' : 'primary'}
                            variant="outlined"
                        />
                        <Box sx={{ marginLeft: 'auto' }}>
                            {isCollapsed ? <ExpandMore /> : <ExpandLess />}
                        </Box>
                    </Box>
                    
                    {/* Expected File Type Badges - only show for source files */}
                    {Object.keys(expectedFiles).length > 0 && fileType === 'source' && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                            {Object.entries(expectedFiles).map(([fileType, expectedHeaders]) => {
                                const isSatisfied = isExpectedFileSatisfied(expectedHeaders, files);
                                return (
                                    <Chip
                                        key={fileType}
                                        label={fileType}
                                        size="small"
                                        variant="outlined"
                                        color={isSatisfied ? 'success' : 'default'}
                                        sx={{
                                            fontSize: '11px',
                                            height: '20px',
                                            '& .MuiChip-label': {
                                                px: 1
                                            }
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    )}
                    
                    <Collapse in={!isCollapsed}>
                        <Stack spacing={1}>
                            {/* Render single FileHeaderLoader component for sequential loading */}
                            {currentLoadingFile && (
                                <FileHeaderLoader
                                    key={`header-loader-${currentLoadingFile.file_name}`}
                                    file={currentLoadingFile}
                                    fileType={fileType}
                                    organizationId={organizationId}
                                    onHeadersLoaded={handleHeadersLoaded}
                                    historyDate={historyDate}
                                />
                            )}
                            {/* Render single FileMetadataLoader component for sequential loading */}
                            {currentLoadingMetadataFile && (
                                <FileMetadataLoader
                                    key={`metadata-loader-${currentLoadingMetadataFile.file_name}`}
                                    file={currentLoadingMetadataFile}
                                    fileType={fileType}
                                    organizationId={organizationId}
                                    onMetadataLoaded={handleMetadataLoaded}
                                    historyDate={historyDate}
                                />
                            )}
                            {files.map((file, index) => (
                                <FileItem 
                                    key={index}
                                    file={file}
                                    pendingReplacement={pendingReplacement}
                                    organizationId={organizationId}
                                    fileType={fileType}
                                    fileHeaderResponse={fileHeaders[file.file_name]}
                                    fileMetadataResponse={fileMetadata[file.file_name]}
                                />
                            ))}
                        </Stack>
                    </Collapse>
                </Stack>
            </CardContent>
        </Card>
    );
}

export default FileGroupingCard;